/**
 * AK-445 — сборка зон поля из фигур зонального редактора.
 *
 * Чистый слой (без карты/DOM/terra-draw): берёт НАБОР фигур, нарисованных в
 * редакторе (круги, прямоугольники, полигоны — сколько угодно), обрезает каждую
 * эталонным контуром поля и добавляет зону-остаток. Результат POST'ится в
 * существующий /fields/{id}/subzones (модель/бэкенд не трогаем).
 *
 * Почему обрезка обязательна: `field_subzones` триггером запрещает зоне
 * выходить за поле только косвенно, но НЕ разрешает зонам пересекаться
 * (`ST_Intersects AND NOT ST_Touches` → EXCEPTION). Обрезанные фигуры + остаток
 * образуют разбиение (соприкасаются, но не перекрываются) — вставка проходит.
 * Пересечение самих фигур между собой ловим здесь и объясняем словами, а не
 * ждём 500 от триггера.
 *
 * Что было до этого (баг AK-445): жёстко один вписанный круг из
 * `buildPivotSplit` — второй круг полива на поле нарисовать было нельзя,
 * вырезание не работало. Теперь фигур N, инструменты — боевые (те же, что в
 * редакторе контура на /map).
 *
 * turf-операции только через уже подключённые пакеты: пересечение считаем как
 * A ∩ B = A − (A − B) (два difference), чтобы не тянуть @turf/intersect.
 */
import area from '@turf/area';
import union from '@turf/union';
import difference from '@turf/difference';
import { featureCollection, multiPolygon, polygon } from '@turf/helpers';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import { extractPolygons, type GeoLike } from './geometryLogic';
import type { SubzoneKind } from './types';

/** Кольцо [[lng,lat], …] (замкнутое). */
export type Ring = number[][];
/** Одна фигура редактора: внешнее кольцо + дырки. */
export type ShapeParts = Ring[];
/** Набор фигур редактора (то, что отдаёт ContourEditorController). */
export type EditorShapes = ShapeParts[];

const M2_PER_HA = 10_000;
/** Меньше 100 м² — обрезок клипа, а не зона (не заводим на него посев). */
const MIN_ZONE_HA = 0.01;
/** Допуск «касание, а не перекрытие» — как ST_Touches в триггере field_subzones. */
const OVERLAP_EPS_M2 = 1;
/** Кругом считаем только достаточно «частую» фигуру (наш круг — 48 сегментов). */
const CIRCLE_MIN_VERTICES = 20;
/** Разброс радиуса относительно среднего, при котором фигура ещё круг. */
const CIRCLE_TOLERANCE = 0.08;

// ── распознавание круга ────────────────────────────────────────────────────

function samePoint(a: number[], b: number[]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/** Радиусы от центра почти одинаковы (с масштабом kx по долготе)? */
function radiusIsConstant(pts: Ring, cx: number, cy: number, kx: number): boolean {
  let min = Infinity;
  let max = 0;
  let sum = 0;
  for (const [x, y] of pts) {
    const r = Math.hypot((x - cx) * kx, y - cy);
    if (r < min) min = r;
    if (r > max) max = r;
    sum += r;
  }
  const mean = sum / pts.length;
  if (!(mean > 0)) return false;
  return (max - min) / mean <= CIRCLE_TOLERANCE;
}

/**
 * Похожа ли фигура на круг полива (пивот). Проверяем в двух метриках:
 * «сырой» градусной (так строит круг шаблон «Полив-круг + углы») и
 * геодезически скорректированной по cos(широты) (так строит круг инструмент
 * «Круг» в редакторе). Достаточно совпадения в любой — обе дают настоящий
 * круг на местности, различается только способ построения.
 */
export function isCircleLike(ring: Ring | undefined): boolean {
  if (!ring || ring.length < 4) return false;
  const pts =
    ring.length > 1 && samePoint(ring[0], ring[ring.length - 1])
      ? ring.slice(0, -1)
      : ring;
  if (pts.length < CIRCLE_MIN_VERTICES) return false;
  let sx = 0;
  let sy = 0;
  for (const [x, y] of pts) {
    sx += x;
    sy += y;
  }
  const cx = sx / pts.length;
  const cy = sy / pts.length;
  const kx = Math.cos((cy * Math.PI) / 180) || 1;
  return radiusIsConstant(pts, cx, cy, 1) || radiusIsConstant(pts, cx, cy, kx);
}

// ── turf-хелперы ───────────────────────────────────────────────────────────

type AnyPoly = Feature<Polygon | MultiPolygon>;

/** Эталонный контур поля как turf-фича (Polygon / MultiPolygon). */
export function contourFeature(contour?: GeoLike | null): AnyPoly | null {
  const polys = extractPolygons(contour);
  if (!polys.length) return null;
  try {
    return polys.length === 1 ? polygon(polys[0]) : multiPolygon(polys);
  } catch {
    return null;
  }
}

/** Фигура редактора как turf-фича. null — вырожденная. */
function shapeFeature(shape: ShapeParts): Feature<Polygon> | null {
  const rings = (shape ?? []).filter((r) => r && r.length >= 4);
  if (!rings.length) return null;
  try {
    return polygon(rings);
  } catch {
    return null;
  }
}

function safeDifference(a: AnyPoly, b: AnyPoly): AnyPoly | null {
  try {
    return difference(featureCollection([a, b])) as AnyPoly | null;
  } catch {
    return null;
  }
}

function safeUnion(a: AnyPoly, b: AnyPoly): AnyPoly | null {
  try {
    return union(featureCollection([a, b])) as AnyPoly | null;
  } catch {
    return null;
  }
}

/** Площадь фичи в гектарах (геодезическая). */
function areaHa(f: AnyPoly): number {
  try {
    return area(f) / M2_PER_HA;
  } catch {
    return 0;
  }
}

/**
 * A ∩ B через два difference: A − (A − B). Отдельный @turf/intersect не нужен.
 * null — фигуры не пересекаются вовсе.
 */
function intersect(a: AnyPoly, b: AnyPoly): AnyPoly | null {
  const outside = safeDifference(a, b);
  if (!outside) return a; // A целиком внутри B
  return safeDifference(a, outside);
}

// ── план зон ───────────────────────────────────────────────────────────────

export interface ZoneDraft {
  kind: SubzoneKind;
  name: string;
  geojson: Polygon | MultiPolygon;
  areaHa: number;
  /** Зона получена из круга (пивот) — для подписи/цвета. */
  isCircle: boolean;
  /** Зона-остаток «богара» (контур минус фигуры). */
  isRest: boolean;
  /**
   * Индекс фигуры редактора, из которой получилась зона, — по нему панель
   * связывает строку с полем ввода имени. `null` — зона-остаток (богара) и
   * объединённая зона из нескольких кругов.
   */
  shapeIndex: number | null;
}

export type ZonePlan =
  | { ok: true; zones: ZoneDraft[]; skipped: number }
  | { ok: false; error: string };

export interface ZonePlanOptions {
  /**
   * Имена, заданные агрономом, по индексу ФИГУРЫ (не зоны). Пустая строка /
   * undefined → берём имя по умолчанию («Орошаемый круг N» / «Зона N»).
   */
  names?: Array<string | undefined>;
  /**
   * Собрать все круги в ОДНУ зону-мультиполигон (founder AC-4: «два круга
   * можно объединить в мультиполигон»). По умолчанию выключено — каждый круг
   * остаётся отдельной именованной зоной (канонический сценарий founder).
   */
  mergePivots?: boolean;
  /** Имя зоны-остатка. По умолчанию «Богара» (или «Остальное поле» без кругов). */
  restName?: string;
  /**
   * Уже сохранённые АКТИВНЫЕ зоны поля — лента `field_subzones` на момент
   * открытия редактора (AK-494, сценарий 2 founder: «вырезать круг →
   * сохранить → вырезать второй круг → сохранить»).
   *
   * Редактор зон каждую сессию стартует с пустого набора фигур (не помнит
   * прошлые круги) и всегда режет от ЭТАЛОННОГО контура поля целиком. Без
   * этого поля:
   *
   *   1. Новая фигура может физически лечь ВНУТРЬ уже сохранённой зоны
   *      (обычный случай: круг + «Богара» на всю оставшуюся площадь уже
   *      покрывают 100% поля) — раньше это ловил только бэкенд-триггер
   *      `field_subzones` («overlaps a sibling») уже ПОСЛЕ round-trip'а, 409
   *      с формулировкой не для агронома. Теперь — понятная ошибка здесь,
   *      ДО сохранения (см. ниже по коду), с явной подсказкой что делать
   *      (удалить/уменьшить старую зону в списке — эта возможность в UI уже
   *      есть).
   *   2. Если агроном СНАЧАЛА удалил старую «Богару» (значит, существующие
   *      зоны больше не покрывают всё поле) — новая фигура законно попадает
   *      в освободившуюся площадь. «Остаток» этой сессии обязан исключать
   *      площадь ОСТАВШИХСЯ существующих зон (напр. первый круг), иначе он
   *      попытается заново занять уже занятую площадь первого круга.
   *
   * Это ДРУГОЙ баг, не GeometryCollection: конфликт геометрии между валидными
   * полигонами, а не искажение геометрии на нормализации.
   */
  existingZones?: Array<GeoLike | null | undefined> | null;
}

/** Объединяет несколько фигур в одну (или null, если нечего объединять). */
function unionAll(feats: AnyPoly[]): AnyPoly | null {
  if (!feats.length) return null;
  let acc: AnyPoly | null = feats[0];
  for (let i = 1; i < feats.length && acc; i++) {
    acc = safeUnion(acc, feats[i]);
  }
  return acc;
}

/** Имя круга по умолчанию: «Орошаемый круг» / «Орошаемый круг N». */
export function defaultCircleName(no: number, total: number): string {
  return total > 1 ? `Орошаемый круг ${no}` : 'Орошаемый круг';
}

/**
 * Собирает зоны из фигур редактора:
 *   1. каждая фигура обрезается контуром поля (вылезшее за поле отбрасывается);
 *   2. фигуры, не попавшие в поле / выродившиеся в обрезок — пропускаются;
 *   3. фигуры не должны перекрываться между собой (иначе внятная ошибка);
 *   4. остаток «контур − все фигуры» становится одной зоной (богара).
 *
 * Круги (любое их количество) → `irrigated_pivot` «Орошаемый круг N»,
 * остальные фигуры → нейтральный `block` «Зона N», остаток → `dryland_corner`.
 * Имя каждой зоны агроном может переопределить (`options.names`).
 */
export function buildZonePlan(
  contour: GeoLike | null | undefined,
  shapes: EditorShapes,
  options: ZonePlanOptions = {},
): ZonePlan {
  const ref = contourFeature(contour);
  if (!ref) {
    return { ok: false, error: 'У поля нет контура — зоны строить не из чего.' };
  }
  if (!shapes.length) {
    return {
      ok: false,
      error: 'Нарисуйте хотя бы одну зону — круг, прямоугольник или полигон.',
    };
  }

  const clipped: Array<{
    feat: AnyPoly;
    isCircle: boolean;
    ha: number;
    shapeIndex: number;
  }> = [];
  let skipped = 0;
  for (let i = 0; i < shapes.length; i++) {
    const shape = shapes[i];
    const feat = shapeFeature(shape);
    if (!feat) {
      skipped += 1;
      continue;
    }
    const inside = intersect(feat, ref);
    if (!inside) {
      skipped += 1; // фигура целиком за пределами поля
      continue;
    }
    const ha = areaHa(inside);
    if (ha < MIN_ZONE_HA) {
      skipped += 1;
      continue;
    }
    clipped.push({ feat: inside, isCircle: isCircleLike(shape[0]), ha, shapeIndex: i });
  }

  if (!clipped.length) {
    return {
      ok: false,
      error: 'Ни одна фигура не попала внутрь поля — рисуйте зоны по контуру.',
    };
  }

  // Зоны не могут перекрываться (триггер field_subzones это запрещает).
  for (let i = 0; i < clipped.length; i++) {
    for (let j = i + 1; j < clipped.length; j++) {
      const both = intersect(clipped[i].feat, clipped[j].feat);
      if (both && areaHa(both) * M2_PER_HA > OVERLAP_EPS_M2) {
        return {
          ok: false,
          error:
            'Фигуры накладываются друг на друга. Объедините их или разведите границы.',
        };
      }
    }
  }

  // AK-494 сценарий 2 («вырезать круг → сохранить → вырезать второй →
  // сохранить»): редактор каждую сессию режет от контура поля ЦЕЛИКОМ и не
  // помнит фигуры прошлой сессии. Если прошлая сессия уже разбила ВСЁ поле
  // (обычный случай: круг + «Богара» на всю оставшуюся площадь), то новая
  // фигура физически ложится ВНУТРЬ уже сохранённой зоны — это не «баг
  // геометрии», а реальный конфликт данных, который иначе поймал бы бэкенд
  // триггером `field_subzones` («overlaps a sibling») уже ПОСЛЕ round-trip'а
  // и куда менее внятной формулировкой. Ловим здесь и объясняем, что делать:
  // сначала освободить область (удалить/уменьшить существующую зону), потом
  // рисовать новую. Если прошлая «Богара» уже удалена агрономом — существующие
  // зоны не покрывают всё поле, наложения не будет, и это ветка не сработает.
  const existingFeat = unionAll(
    (options.existingZones ?? [])
      .map((g) => contourFeature(g))
      .filter((f): f is AnyPoly => !!f),
  );
  if (existingFeat) {
    for (const c of clipped) {
      const both = intersect(c.feat, existingFeat);
      if (both && areaHa(both) * M2_PER_HA > OVERLAP_EPS_M2) {
        return {
          ok: false,
          error:
            'Эта область поля уже занята сохранённой зоной. Чтобы добавить новую фигуру здесь, ' +
            'сначала удалите или уменьшите существующую зону (например, «Богару») в списке зон поля.',
        };
      }
    }
  }

  const circles = clipped.filter((c) => c.isCircle).length;
  const named = (i: number, fallback: string): string => {
    const raw = options.names?.[i]?.trim();
    return raw ? raw : fallback;
  };

  let circleNo = 0;
  let otherNo = 0;
  const zones: ZoneDraft[] = [];
  // Круги, объединяемые в одну зону (AC-4), собираем отдельно.
  const toMerge: Array<{ feat: AnyPoly; ha: number; shapeIndex: number }> = [];

  for (const c of clipped) {
    if (c.isCircle) {
      circleNo += 1;
      if (options.mergePivots && circles > 1) {
        toMerge.push({ feat: c.feat, ha: c.ha, shapeIndex: c.shapeIndex });
        continue;
      }
      zones.push({
        kind: 'irrigated_pivot',
        name: named(c.shapeIndex, defaultCircleName(circleNo, circles)),
        geojson: c.feat.geometry,
        areaHa: c.ha,
        isCircle: true,
        isRest: false,
        shapeIndex: c.shapeIndex,
      });
      continue;
    }
    otherNo += 1;
    zones.push({
      kind: 'block',
      name: named(c.shapeIndex, `Зона ${otherNo}`),
      geojson: c.feat.geometry,
      areaHa: c.ha,
      isCircle: false,
      isRest: false,
      shapeIndex: c.shapeIndex,
    });
  }

  if (toMerge.length) {
    // Несмежные круги → одна зона-МУЛЬТИПОЛИГОН. Бэкенд принимает: subzones
    // кладёт геометрию через ST_Multi(ST_MakeValid(ST_GeomFromGeoJSON(...))).
    let acc: AnyPoly | null = toMerge[0].feat;
    for (let i = 1; i < toMerge.length && acc; i++) {
      acc = safeUnion(acc, toMerge[i].feat);
    }
    if (!acc) {
      return { ok: false, error: 'Не удалось объединить круги в одну зону.' };
    }
    zones.unshift({
      kind: 'irrigated_pivot',
      // Имя берём от первого круга, если агроном его задал.
      name: named(toMerge[0].shapeIndex, 'Орошаемый'),
      geojson: acc.geometry,
      areaHa: toMerge.reduce((s, m) => s + m.ha, 0),
      isCircle: true,
      isRest: false,
      shapeIndex: null,
    });
  }

  // Остаток поля = контур − объединение всех НОВЫХ зон − уже занятое прошлыми
  // сессиями (AK-494 сценарий 2: без вычета existingFeat второй «остаток»
  // переоткрывает площадь, которую уже заняла первая сохранённая зона).
  let merged: AnyPoly | null = clipped[0].feat;
  for (let i = 1; i < clipped.length && merged; i++) {
    merged = safeUnion(merged, clipped[i].feat);
  }
  let rest = merged ? safeDifference(ref, merged) : null;
  if (rest && existingFeat) {
    rest = safeDifference(rest, existingFeat);
  }
  if (rest) {
    const restHa = areaHa(rest);
    if (restHa >= MIN_ZONE_HA) {
      zones.push({
        kind: 'dryland_corner',
        // Богара — неполивная часть поля. Если кругов нет, слово «богара»
        // агроному ничего не скажет — называем нейтрально.
        name: options.restName?.trim() || (circles > 0 ? 'Богара' : 'Остальное поле'),
        geojson: rest.geometry,
        areaHa: restHa,
        isCircle: false,
        isRest: true,
        shapeIndex: null,
      });
    }
  }

  return { ok: true, zones, skipped };
}
